import type { WhIssue, WhSeverityDef, WhSeverityKey } from './types';

export const WH_SEVERITY: Record<WhSeverityKey, WhSeverityDef> = {
  critical: { label: 'Critical', tone: 'terra', icon: 'alert' },
  important: { label: 'Important', tone: 'gold', icon: 'flame' },
  minor: { label: 'Minor', tone: 'info', icon: 'info' },
  good: { label: 'Looks good', tone: 'success', icon: 'check-circ' },
};

export const WH_ISSUES: WhIssue[] = [
  {
    id: 1,
    severity: 'critical',
    title: 'Your website is slow on phones',
    oneLiner:
      'Your site takes 5.2 seconds to load on mobile — most patients will leave before it finishes.',
    patientsLost: 35,
    problem:
      'When someone searches “dentist near me” on their phone, your website takes 5.2 seconds to fully load. The industry standard is under 2.5 seconds. Google measures this and considers it when deciding where to rank your site.',
    effect:
      'More than half your potential patients visit on their phones. Studies show 53% of mobile users leave a site that takes over 3 seconds to load. This means roughly 35 potential patients per month are clicking your site, seeing a blank loading screen, and going to a competitor instead.',
    solution:
      'Your images need to be compressed (many are 3-5x larger than necessary), and your site needs “lazy loading” — images below the screen don’t load until the patient scrolls to them. A developer can usually fix this in a few hours.',
    pages: ['/', '/services', '/about', '/contact'],
  },
  {
    id: 2,
    severity: 'critical',
    title: 'No online booking on your website',
    oneLiner:
      'Patients have to call to book — 67% of people prefer booking online, especially after hours.',
    patientsLost: 28,
    problem:
      'Your website doesn’t have an online booking widget. The only way for patients to schedule is by calling during office hours. There’s no way to book at night, on weekends, or when your phones are busy.',
    effect:
      '67% of patients prefer booking online. When someone finds your site at 9 PM with a toothache, they can’t book — so they go to a competitor who has online scheduling. You’re losing an estimated 28 potential bookings per month, mostly after-hours and weekend searchers.',
    solution:
      'Add an online booking widget that connects to your practice management software so appointments appear on your schedule automatically. ELVA can recommend booking tools that integrate with your current system. It typically takes 1-2 days to set up.',
    pages: ['/', '/services', '/emergency', '/contact'],
  },
  {
    id: 3,
    severity: 'critical',
    title: 'Your site doesn’t tell Google what services you offer',
    oneLiner:
      'Missing “schema markup” means Google can’t show your services, hours, or reviews in search results.',
    patientsLost: 20,
    problem:
      'When Google reads your website, it relies on hidden tags called “schema markup” to understand what your practice offers. Your site is missing these entirely. Google can read your text, but can’t confidently connect your practice to searches like “Invisalign provider near me.”',
    effect:
      'Without these tags, Google won’t show your star rating, office hours, or services directly in search results. Competitors who have this show up with rich listings — reviews, hours, phone number — while yours shows a plain blue link. You’re losing roughly 20 clicks per month.',
    solution:
      'A developer needs to add dental practice schema markup: your services list, office hours, accepted insurance, doctor names, and review ratings. It’s invisible to patients but tells Google exactly what you do. Usually takes half a day.',
    pages: ['All pages'],
  },
  {
    id: 4,
    severity: 'important',
    title: 'No dedicated page for each service you offer',
    oneLiner:
      'You list all services on one page — Google can’t rank you for specific treatments like Invisalign or implants.',
    patientsLost: 15,
    problem:
      'Your website has a single “Services” page that lists everything — cleanings, implants, Invisalign, whitening, emergency care — all on one page. There’s no dedicated page for each treatment.',
    effect:
      'When a patient searches “dental implants in Brampton,” Google looks for pages specifically about dental implants. Your competitor has a full page; you have one bullet point on a long list. Google ranks the dedicated page higher almost every time — missing roughly 15 patients/month.',
    solution:
      'Create a separate page for each major service: Implants, Invisalign, Cosmetic Dentistry, Emergency Care, Teeth Whitening. Each should have a description, what to expect, approximate pricing, and a booking button. ELVA can generate these pages for you.',
    pages: ['/services'],
  },
  {
    id: 5,
    severity: 'important',
    title: 'Your Google Business Profile is only 70% complete',
    oneLiner: 'Missing photos, services, and Q&A answers hurt your ranking in Google Maps results.',
    patientsLost: 12,
    problem:
      'Your Google Business Profile is missing recent photos of your office, a complete services list, answers to patient questions, and your accepted insurance list. It also hasn’t been updated in 3 months.',
    effect:
      'Google ranks more complete profiles higher in Maps results. When someone searches “dentist near me,” Maps shows 3 local results — the “Map Pack.” You’re currently #5 for your top keyword. Moving to #3 could bring 12 more calls per month.',
    solution:
      'Update your Profile weekly: add 5-10 new photos of your office and team, list every service, respond to all patient questions, and add your insurance list. ELVA can help automate posting updates and remind you when photos are needed.',
    pages: ['Google Business Profile'],
  },
  {
    id: 6,
    severity: 'important',
    title: 'No Spanish-language pages',
    oneLiner: '30% of your neighborhood speaks Spanish — but your entire website is English-only.',
    patientsLost: 10,
    problem:
      'Your practice is in an area where 30% of residents speak Spanish as their primary language. Your website has no Spanish content — no translated pages, no Spanish landing pages, and no mention that your staff speaks Spanish.',
    effect:
      'Spanish-speaking patients searching “dentista cerca de mi” won’t find you. Even if they find your English site, the language barrier makes them less likely to book. You have 2 bilingual staff, but patients don’t know that — losing an estimated 10 patients/month.',
    solution:
      'Create Spanish versions of your key pages: Homepage, Emergency, Services, and Contact. At minimum, add a Spanish landing page that highlights your bilingual staff. ELVA can generate these pages and the corresponding Spanish Google Ads.',
    pages: ['/', '/emergency', '/services'],
  },
  {
    id: 7,
    severity: 'minor',
    title: 'Missing alt text on images',
    oneLiner:
      'Your images don’t have descriptions — Google can’t “see” them, and screen readers can’t read them.',
    patientsLost: 5,
    problem:
      '42 of your 58 website images are missing “alt text” — short descriptions that tell Google and screen readers what the image shows. Your before/after photos, office images, and team photos all lack these.',
    effect:
      'Google uses alt text to understand images and can surface them in search results. Missing alt text is also an accessibility issue. The SEO impact is moderate but adds up: roughly 5 patients/month could find you through Google Image searches if labeled properly.',
    solution:
      'Add descriptive alt text to all images. Instead of “IMG_4521.jpg,” the alt text should say “Before and after teeth whitening results at Smile Dental.” ELVA can generate suggested alt text for each image.',
    pages: ['All pages'],
  },
  {
    id: 8,
    severity: 'minor',
    title: 'Blog hasn’t been updated in 5 months',
    oneLiner:
      'Google favors websites that publish fresh content regularly — your last post was November 2025.',
    patientsLost: 8,
    problem:
      'Your blog has 12 articles, but the most recent was published 5 months ago. Google considers “freshness” when ranking content — regularly updated sites signal that the business is active and current.',
    effect:
      'Your existing posts still rank for some keywords, but they’re slowly losing position to competitors who publish weekly. Fresh, relevant articles attract patients searching for information, who then book. Estimated impact: 8 potential patients/month.',
    solution:
      'Publish 2-4 blog posts per month on topics patients search for. ELVA can auto-generate these based on local keyword gaps — “How much do dental implants cost in Brampton?” or “Emergency dentist: ER vs. dentist.” You just review and approve.',
    pages: ['/blog'],
  },
  {
    id: 9,
    severity: 'good',
    title: 'HTTPS is properly configured',
    oneLiner:
      'Your site has a valid security certificate — patients see the padlock icon and Google trusts your site.',
  },
  {
    id: 10,
    severity: 'good',
    title: 'Mobile-friendly layout',
    oneLiner:
      'Your site adapts to phone screens correctly — text is readable and buttons are tappable without zooming.',
  },
];

export function computeIssueCounts(issues: WhIssue[]) {
  const counts = { critical: 0, important: 0, minor: 0, good: 0, lost: 0 };
  for (const issue of issues) {
    counts[issue.severity]++;
    counts.lost += issue.patientsLost ?? 0;
  }
  return counts;
}

export function computeHealthScore(counts: ReturnType<typeof computeIssueCounts>) {
  return Math.max(0, 100 - counts.critical * 18 - counts.important * 8 - counts.minor * 3);
}
